<script setup lang="ts">
import { COUNTRIES_EXCL_IMPORT, FREE_SHIPPING_UNTIL, FREE_SHIPPING_UNTIL_COUNTRIES } from '~/configuration/global.configuration'
import { useCartStore } from '~/stores/cart'
import { useStorefrontStore } from '~/stores/storefront'
import { useStoryblokStore } from '~/stores/storyblok'

const cartStore = useCartStore()
const storefrontStore = useStorefrontStore()
const storyblokStore = useStoryblokStore()
const { freeShippingThreshold } = storeToRefs(storefrontStore)

const { state: cartState } = storeToRefs(cartStore)
const { $t, $price } = useNuxtApp()

function isFreeShippingFromCMS() {
  const thresholdDateConstant = storyblokStore.getConstant(FREE_SHIPPING_UNTIL)
  const thresholdCountriesConstant = storyblokStore.getConstant(FREE_SHIPPING_UNTIL_COUNTRIES)

  const isCountryIncluded = thresholdCountriesConstant
    && thresholdCountriesConstant.value
    && (thresholdCountriesConstant.value === 'all' || thresholdCountriesConstant.value.includes(storefrontStore.currentMarket.storefrontCode))

  if (thresholdDateConstant && isCountryIncluded) {
    const today = new Date()
    const thresholdDate = new Date(thresholdDateConstant.value)
    if (today < thresholdDate)
      return true
  }

  return false
}

const progressToFreeShipping = computed(() => {
  if (cartState.value.isFreeShippingDiscountCodeApplied)
    return 1

  if (isFreeShippingFromCMS())
    return 1

  const decimal
    = (cartState.value.priceTotal - cartState.value.priceShipping) / freeShippingThreshold.value
  return decimal < 1 ? decimal : 1
})

const countriesExclImport = computed(() => {
  const countries = storyblokStore.getConstant(COUNTRIES_EXCL_IMPORT)
  return (countries && countries.value.toUpperCase()) ?? ''
})

const freeShippingMessage = computed(() => {
  if (
    countriesExclImport.value.includes(storefrontStore.currentMarket.countryCode)
  ) {
    return $t('freeShippingIsAcquiredExclImport')
  }

  return $t('freeShippingIsAcquired')
})

const message = computed(() => {
  if (freeShippingThreshold.value === -1)
    return undefined

  if (freeShippingThreshold.value === 0)
    return $t('shippingIsFreeAndIncluded')

  if (progressToFreeShipping.value >= 1)
    return freeShippingMessage.value

  return $t('remainingValueToGetFreeShipping', {
    diff: $price(freeShippingThreshold.value * (1 - progressToFreeShipping.value) || 0),
  })
})
</script>

<template>
  <section class="shipping-info">
    <span
      class="message"
      data-test="free-shipping-text"
    >{{ message }}</span>

    <div class="shipping-bar">
      <progress
        :value="progressToFreeShipping"
        max="1"
        data-test="progress-bar"
      />
    </div>
  </section>
</template>

<style scoped>
.shipping-info {
  text-align: center;
  margin-bottom: 2rem;
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.shipping-bar {
  width: 100%;
  height: 0.8rem;
  flex-shrink: 0;
  position: relative;
}

progress[value]::-webkit-progress-bar {
  background: var(--gray-light);
}

progress,
progress::-webkit-progress-bar {
  width: 100%;
  height: 100%;
  border: none;
  appearance: none;
  background: var(--white);
  border-radius: 1rem;
}

progress::-moz-progress-bar {
  background: var(--green);
}
progress[value]::-webkit-progress-value {
  background-size: 100%;
  background-color: var(--green);
  border-radius: 1rem;
}

.message {
 font: var(--font-subtitle2);
}
</style>
